import type { SortingState } from "@tanstack/react-table";
import clsx from "clsx";
import { FC, useCallback, useEffect, useMemo, useState } from "react";

import type {
  ColumnFilter,
  FilterSpec,
  FilterType,
} from "@tsmono/inspect-components/columnFilter";
import { ErrorPanel, LoadingBar, ProgressBar } from "@tsmono/react/components";
import { join } from "@tsmono/util";

import { useLogDir } from "../../app_config";
import { scopePrefix } from "../../client/database";
import {
  LogListingRow,
  useLogListing,
  useLogsSync,
  useSamplesListing,
  type SamplesListingRow,
} from "../../log_data";
import { highlightSample } from "../../state/actions";
import { useStore } from "../../state/store";
import { useUserSettings } from "../../state/userSettings";
import { FlowButton } from "../flow/FlowButton";
import { useFlowQuery } from "../flow/hooks";
import { LogListFooter } from "../log-list/LogListFooter";
import { ApplicationNavbar } from "../navbar/ApplicationNavbar";
import { NavbarButton } from "../navbar/NavbarButton";
import { ViewSegmentedControl } from "../navbar/ViewSegmentedControl";
import { useSamplesGridNavigationAction } from "../routing/sampleNavigation";
import { samplesUrl, toFullUrl, useSamplesRouteParams } from "../routing/url";
import { useEvalSet } from "../server/useEvalSet";
import { ColumnSelectorPopover } from "../shared/ColumnSelectorPopover";
import { ExtendedColumnDef } from "../shared/data-grid/columnTypes";
import { type PickerColumn } from "../shared/gridUtils";
import {
  buildSampleColumns,
  SCORE_FIELD_RAW_PREFIX,
} from "../shared/samples-grid/columns";
import { SamplesGrid } from "../shared/samples-grid/SamplesGrid";
import { SampleRow } from "../shared/samples-grid/types";
import { useSampleGridState } from "../shared/samples-grid/useSampleGridState";

import styles from "./SamplesPanel.module.css";

// Cross-log default: most-recently-completed first (matches the prior AG view).
const kSamplesPanelDefaultSorting: SortingState = [
  { id: "completed_at", desc: true },
];

// Stable empties for unseeded persisted state — a fresh `{}` per render would
// invalidate the grid's memos every render.
const kNoColumnFilters: Record<string, ColumnFilter> = Object.freeze({});
const kNoColumnSizing: Record<string, number> = Object.freeze({});

const sampleRowId = (
  logFile: string,
  sampleId: string | number,
  epoch: number
) => `${logFile}-${sampleId}-${epoch}`.replace(/\s+/g, "_");

// Picker-column shim of the column list for `useSampleGridState` /
// `ColumnSelectorPopover`, which key off `colId` / `headerName`.
const toPickerColumns = (
  columns: ExtendedColumnDef<SampleRow>[]
): PickerColumn[] =>
  columns.map((col) => ({
    colId: col.id,
    headerName: typeof col.header === "string" ? col.header : "",
  }));

const completedAtTime = (row: SampleRow): number => {
  const v = row.data?.completed_at;
  return v ? new Date(v).getTime() : 0;
};

const kNoSamplesRows: SamplesListingRow[] = [];
const kNoLogRows: LogListingRow[] = [];

export const SamplesPanel: FC = () => {
  const { samplesPath } = useSamplesRouteParams();
  const logDir = useLogDir();

  // Sync the listing for this panel's scope; busy/error derive from its status.
  const listing = useLogsSync(logDir, samplesPath ?? "");
  const showRetriedLogs = useUserSettings((state) => state.showRetriedLogs);
  const setShowRetriedLogs = useUserSettings(
    (state) => state.setShowRetriedLogs
  );

  const filteredSamplesCount = useStore(
    (state) => state.log.filteredSampleCount
  );
  const setFilteredSampleCount = useStore(
    (state) => state.logActions.setFilteredSampleCount
  );
  const setDisplayedSamples = useStore(
    (state) => state.logsActions.setDisplayedSamples
  );
  const clearDisplayedSamples = useStore(
    (state) => state.logsActions.clearDisplayedSamples
  );
  const previousSamplesPath = useStore(
    (state) => state.logs.samplesListState.previousSamplesPath
  );
  const setPreviousSamplesPath = useStore(
    (state) => state.logsActions.setPreviousSamplesPath
  );

  const highlightedSample = useStore((state) => state.log.highlightedSample);

  const [showColumnSelector, setShowColumnSelector] = useState(false);
  const [columnButtonEl, setColumnButtonEl] =
    useState<HTMLButtonElement | null>(null);

  const flowData = useFlowQuery(samplesPath || "").data;

  const currentDir = join(samplesPath || "", logDir);

  // Every sample summary under this panel's scope, each row carrying its
  // log's display context (the subsystem joins; no by-name lookups here).
  const samplesListing = useSamplesListing({
    logDir,
    scope: { prefix: currentDir },
  });
  const scopedSamples = samplesListing.data ?? kNoSamplesRows;

  const evalSet = useEvalSet(samplesPath || "").data;
  const logListing = useLogListing(logDir);
  const logFiles = logListing.data ?? kNoLogRows;
  const error = listing.error ?? samplesListing.error ?? logListing.error;

  const currentDirLogFiles = useMemo(() => {
    const files = [];
    for (const logFile of logFiles) {
      const inCurrentDir = logFile.name.startsWith(scopePrefix(currentDir));
      const skipped = !showRetriedLogs && logFile.retried;
      if (inCurrentDir && !skipped) {
        files.push(logFile);
      }
    }
    return files;
  }, [currentDir, logFiles, showRetriedLogs]);

  const totalTaskCount = useMemo(() => {
    const currentDirTaskIds = new Set(currentDirLogFiles.map((f) => f.task_id));
    let count = currentDirLogFiles.length;
    for (const task of evalSet?.tasks || []) {
      if (!currentDirTaskIds.has(task.task_id)) {
        count++;
      }
    }
    return count;
  }, [currentDirLogFiles, evalSet]);

  const completedTaskCount = useMemo(() => {
    let count = 0;
    for (const logFile of currentDirLogFiles) {
      if (logFile.status !== undefined && logFile.status !== "started") {
        count++;
      }
    }
    return count;
  }, [currentDirLogFiles]);

  // Build the superset of columns.
  const allColumns = useMemo(
    () =>
      buildSampleColumns({
        viewMode: "grid",
        multiLog: true,
        samples: scopedSamples.map((row) => row.summary),
      }),
    [scopedSamples]
  );

  const pickerColumns = useMemo(
    () => toPickerColumns(allColumns),
    [allColumns]
  );

  // Default visibility for unseeded columns. `error`/`limit`/`retries`/
  // `fallbacks` auto-promote to visible when at least one sample carries
  // that field.
  const optionalHasData = useMemo(() => {
    let error = false,
      limit = false,
      retries = false,
      fallbacks = false;
    for (const { summary } of scopedSamples) {
      if (summary.error) error = true;
      if (summary.limit) limit = true;
      if (summary.retries) retries = true;
      if (summary.model_fallbacks?.length) fallbacks = true;
      if (error && limit && retries && fallbacks) break;
    }
    return { error, limit, retries, fallbacks };
  }, [scopedSamples]);

  const defaultsForUnseededColumns = useCallback(
    (col: PickerColumn) => {
      const id = col.colId;
      if (id === "error") return optionalHasData.error;
      if (id === "limit") return optionalHasData.limit;
      if (id === "retries") return optionalHasData.retries;
      if (id === "fallbacks") return optionalHasData.fallbacks;
      // `created` defaults off — many users won't care.
      if (id === "created") return false;
      if (id === "sampleUuid") return false;
      if (id === "cost") return false;
      return true;
    },
    [optionalHasData]
  );

  const {
    columnVisibility,
    setColumnVisibility,
    sorting: persistedSorting,
    columnFilters: persistedFilters,
    columnSizing: persistedSizing,
    patchGridState,
  } = useSampleGridState("samplesPanel", pickerColumns, {
    defaultsForUnseededColumns,
  });

  // Store-backed (rather than left to SamplesGrid's local state) so the
  // navbar's Reset Filters button and the column-selector's filter markers can
  // react to the active per-column filters, and so filters/sort/widths survive
  // the panel unmounting on sample navigation. The grid still filters
  // client-side via `applyFiltersClientSide` — there's no upstream filtrex
  // pass in this view.
  const columnFilters = persistedFilters ?? kNoColumnFilters;
  const sorting = persistedSorting ?? kSamplesPanelDefaultSorting;
  const columnSizing = persistedSizing ?? kNoColumnSizing;

  const handleColumnFilterChange = useCallback(
    (columnId: string, filterType: FilterType, spec: FilterSpec | null) => {
      const next = { ...columnFilters };
      if (spec === null) delete next[columnId];
      else next[columnId] = { columnId, filterType, spec };
      patchGridState({ columnFilters: next });
    },
    [columnFilters, patchGridState]
  );
  const handleSortingChange = useCallback(
    (next: SortingState) => patchGridState({ sorting: next }),
    [patchGridState]
  );
  const handleColumnSizingChange = useCallback(
    (next: Record<string, number>) => patchGridState({ columnSizing: next }),
    [patchGridState]
  );
  const filteredFields = useMemo(
    () => Object.keys(columnFilters),
    [columnFilters]
  );
  const hasFilter = filteredFields.length > 0;
  const handleResetFilters = useCallback(
    () => patchGridState({ columnFilters: {} }),
    [patchGridState]
  );

  // Hiding a column also drops any active filter on it — a hidden column
  // shouldn't keep filtering invisibly (matches the log list's behavior).
  const handleColumnVisibilityChange = useCallback(
    (newVisibility: Record<string, boolean>) => {
      const next = { ...columnFilters };
      let changed = false;
      for (const id of Object.keys(columnFilters)) {
        if (newVisibility[id] === false) {
          delete next[id];
          changed = true;
        }
      }
      if (changed) patchGridState({ columnFilters: next });
      setColumnVisibility(newVisibility);
    },
    [columnFilters, patchGridState, setColumnVisibility]
  );

  // Controlled visibility map keyed by column id, consumed by the DataGrid.
  const visibilityForGrid = useMemo<Record<string, boolean>>(() => {
    const v: Record<string, boolean> = {};
    for (const col of allColumns) {
      const key = col.id ?? "";
      const seeded = columnVisibility[key];
      v[key] = seeded === undefined ? true : seeded;
    }
    return v;
  }, [allColumns, columnVisibility]);

  // eslint-disable-next-line tsmono/no-raw-use-effect -- baselined at rule introduction; migrate to a named hook or derived state
  useEffect(() => {
    if (samplesPath === previousSamplesPath) return;
    if (previousSamplesPath !== undefined) clearDisplayedSamples();
    setPreviousSamplesPath(samplesPath);
  }, [
    samplesPath,
    previousSamplesPath,
    clearDisplayedSamples,
    setPreviousSamplesPath,
  ]);

  // Transform the scoped samples into flat grid rows, pre-sorted by
  // completion time (descending) since interactive sorting is deferred.
  const [sampleRows, hasRetriedLogs] = useMemo(() => {
    let displayIndex = 1;

    const anyLogInCurrentDirCouldBeSkipped = currentDirLogFiles.some(
      (log) => log.retried
    );
    const logInCurrentDirByName = currentDirLogFiles.reduce(
      (acc: Record<string, LogListingRow>, log) => {
        acc[log.name] = log;
        return acc;
      },
      {}
    );

    // A projection: sample-intrinsic derived values come off the listing row
    // (attached at ingestion by `deriveSampleFields`); log-level context is
    // the subsystem's read-time join.
    const allRows: SampleRow[] = scopedSamples.map(
      ({ logFile, summary: sample, derived, log }) => {
        const row: SampleRow = {
          logFile,
          sampleId: sample.id,
          epoch: sample.epoch,
          data: sample,
          created: log.created ?? "",
          task: log.task || "",
          model: log.model || "",
          status: log.status,
          input: derived.input,
          target: derived.target,
          error: sample.error,
          limit: sample.limit,
          limit_reason: sample.limit_reason,
          retries: sample.retries,
          fallbacks: derived.fallbacks,
          completed: sample.completed,
          tokens: derived.tokens,
          cost: derived.cost,
          duration: sample.total_time ?? undefined,
        };
        if (derived.scores) {
          for (const [scoreName, value] of Object.entries(derived.scores)) {
            row[`${SCORE_FIELD_RAW_PREFIX}${scoreName}`] = value;
          }
        }
        return row;
      }
    );

    const _sampleRows = allRows.filter(
      (row) => row.logFile in logInCurrentDirByName
    );
    // Sort by completion time descending, then assign the display index so
    // the `#` column matches the rendered order.
    _sampleRows.sort((a, b) => completedAtTime(b) - completedAtTime(a));
    for (const row of _sampleRows) {
      row.displayIndex = displayIndex++;
    }
    const _hasRetriedLogs =
      _sampleRows.length < allRows.length || anyLogInCurrentDirCouldBeSkipped;

    return [_sampleRows, _hasRetriedLogs];
  }, [scopedSamples, currentDirLogFiles]);

  const { getSampleDetailUrl, navigateToSampleDetail } =
    useSamplesGridNavigationAction();
  const handleRowOpen = useCallback(
    (row: SampleRow) => {
      navigateToSampleDetail(row.logFile, row.sampleId, row.epoch);
    },
    [navigateToSampleDetail]
  );
  const getRowHref = (row: SampleRow) =>
    toFullUrl(getSampleDetailUrl(row.logFile, row.sampleId, row.epoch));

  // Reflect the grid's post-filter/post-sort rows into store-backed
  // displayed-samples state (drives the footer count + cross-tab prev/next
  // navigation). Sourced from the grid — which owns the filter/sort — rather
  // than the unfiltered input, so both stay in sync with what's rendered.
  const handleDisplayedRowsChange = useCallback(
    (rows: SampleRow[]) => {
      setFilteredSampleCount(rows.length);
      setDisplayedSamples(
        rows.map((row) => ({
          logFile: row.logFile,
          sampleId: row.sampleId,
          epoch: row.epoch,
        }))
      );
    },
    [setFilteredSampleCount, setDisplayedSamples]
  );

  const getRowId = useCallback(
    (row: SampleRow) => sampleRowId(row.logFile, row.sampleId, row.epoch),
    []
  );

  // No clearing of the handle on mount/scope change: row ids embed the
  // logFile and rows are scope-filtered, so a handle from another scope
  // matches nothing (inert), while returning from the detail view keeps the
  // selection highlighted.
  const selectedRowId = highlightedSample
    ? sampleRowId(
        highlightedSample.logFile,
        highlightedSample.id,
        highlightedSample.epoch
      )
    : undefined;

  // Keyboard/click selection moves flow to the selection's owner (zustand),
  // which feeds back through selectedRowId — the grid never shadows it.
  const handleRowSelect = useCallback(
    (row: SampleRow) => highlightSample(row.sampleId, row.epoch, row.logFile),
    []
  );

  const isEmptyAndLoading =
    sampleRows.length === 0 && (listing.busy || samplesListing.loading);

  return (
    <div className={clsx(styles.panel)}>
      <ApplicationNavbar
        currentPath={samplesPath}
        fnNavigationUrl={samplesUrl}
        viewerOptionsError={listing.refreshError}
      >
        {hasFilter && (
          <NavbarButton
            key="reset-filters"
            label="Reset Filters"
            icon="filter"
            onClick={handleResetFilters}
          />
        )}

        {hasRetriedLogs && (
          <NavbarButton
            key="show-retried"
            label="Show Retried Logs"
            icon={showRetriedLogs ? "toggleOn" : "toggleOff"}
            latched={showRetriedLogs}
            subtle
            onClick={() => {
              setShowRetriedLogs(!showRetriedLogs);
            }}
          />
        )}
        <NavbarButton
          key="choose-columns"
          ref={setColumnButtonEl}
          label="Columns"
          icon="columns"
          dropdown
          subtle
          onClick={(e) => {
            e.stopPropagation();
            setShowColumnSelector((prev) => !prev);
          }}
        />

        <ViewSegmentedControl selectedSegment="samples" />
        {flowData && <FlowButton />}
      </ApplicationNavbar>

      <ColumnSelectorPopover
        showing={showColumnSelector}
        setShowing={setShowColumnSelector}
        columns={pickerColumns}
        visibility={visibilityForGrid}
        onVisibilityChange={handleColumnVisibilityChange}
        positionEl={columnButtonEl}
        filteredFields={filteredFields}
        scoresHeading="Scores"
      />

      <LoadingBar loading={listing.busy} />
      <div className={clsx(styles.list, "text-size-smaller")}>
        {error ? (
          <ErrorPanel
            title="Error"
            error={{ message: error.message, stack: error.stack }}
          />
        ) : (
          <SamplesGrid
            rowData={sampleRows}
            columnDefs={allColumns}
            columnVisibility={visibilityForGrid}
            columnFilters={columnFilters}
            onColumnFilterChange={handleColumnFilterChange}
            applyFiltersClientSide
            onDisplayedRowsChange={handleDisplayedRowsChange}
            sorting={sorting}
            onSortingChange={handleSortingChange}
            columnSizing={columnSizing}
            onColumnSizingChange={handleColumnSizingChange}
            getRowId={getRowId}
            selectedRowId={selectedRowId}
            onRowSelect={handleRowSelect}
            onRowOpen={handleRowOpen}
            getRowHref={getRowHref}
            loading={isEmptyAndLoading}
          />
        )}
      </div>

      <LogListFooter
        itemCount={filteredSamplesCount ?? 0}
        itemCountLabel={filteredSamplesCount === 1 ? "sample" : "samples"}
        progressText={
          listing.busy
            ? `Syncing${filteredSamplesCount ? ` (${filteredSamplesCount.toLocaleString()} samples)` : ""}`
            : undefined
        }
        progressBar={
          totalTaskCount !== completedTaskCount ? (
            <ProgressBar
              min={0}
              max={totalTaskCount}
              value={completedTaskCount}
              width="100px"
              label={"tasks"}
            />
          ) : undefined
        }
      />
    </div>
  );
};
